import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdmins,
  createAdmin,
  updateAdmin,
  deleteAdmin,
} from '../../features/Users/userSlice';
import { Table, Button, Modal, Form, Input, Select, message, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

const Admins = () => {
  const dispatch = useDispatch();
  const { admins, loading } = useSelector((state) => state.users);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  useEffect(() => {
    dispatch(fetchAdmins({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
  }, [dispatch, pagination.current, pagination.pageSize, statusFilter]);

  const handleCreate = () => {
    form.resetFields();
    setSelectedAdmin(null);
    setIsModalVisible(true);
    setFormErrors({});
  };

  const handleEdit = (admin) => {
    setSelectedAdmin(admin);
    form.setFieldsValue(admin);
    setIsModalVisible(true);
    setFormErrors({});
  };

  const handleDelete = (id) => {
    dispatch(deleteAdmin(id))
      .unwrap()
      .then(() => {
        message.success('Admin deleted successfully');
        // Refresh the list after deletion.
        dispatch(fetchAdmins({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
      })
      .catch(() => {
        message.error('Failed to delete admin');
      });
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedAdmin) {
        // Send only changed fields during update.
        const changedFields = {};
        Object.keys(values).forEach((key) => {
          if (values[key] !== selectedAdmin[key]) {
            changedFields[key] = values[key];
          }
        });
        await dispatch(updateAdmin({ id: selectedAdmin.id, ...changedFields })).unwrap();
        message.success('Admin updated successfully');
      } else {
        await dispatch(createAdmin(values)).unwrap();
        message.success('Admin created successfully');
      }
      setIsModalVisible(false);
      // Refresh the list after creation/update.
      dispatch(fetchAdmins({ page: pagination.current, per_page: pagination.pageSize, status: statusFilter }));
    } catch (error) {
      if (error.errors) {
        setFormErrors(error.errors); // Set form errors from API response.
      } else {
        message.error('An error occurred. Please try again.');
      }
    }
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    // Reset page to 1 when filter changes.
    setPagination({ ...pagination, current: 1 });
    dispatch(fetchAdmins({ page: 1, per_page: pagination.pageSize, status: value }));
  };

  const handleTableChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
    dispatch(fetchAdmins({ page, per_page: pageSize, status: statusFilter }));
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button type="primary" onClick={handleCreate} style={{ marginBottom: 16 }}>
        Add Admin
      </Button>
      <Select
        placeholder="Filter by status"
        onChange={handleFilterChange}
        style={{ width: 200, marginLeft: 16 }}
        allowClear
        value={statusFilter || undefined}
      >
        <Select.Option value="">All</Select.Option>
        <Select.Option value="pending">Pending</Select.Option>
        <Select.Option value="accepted">Accepted</Select.Option>
        <Select.Option value="rejected">Rejected</Select.Option>
      </Select>
      <Table
        dataSource={admins.data} // Data array from Redux state.
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          current: admins.current_page || pagination.current,
          pageSize: admins.per_page || pagination.pageSize,
          total: admins.total || 0,
          showSizeChanger: false,
          onChange: handleTableChange,
        }}
      />

      <Modal
        title={selectedAdmin ? 'Edit Admin' : 'Add Admin'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          {/* Name Field */}
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter the name' }]}
            validateStatus={formErrors.name ? 'error' : ''}
            help={formErrors.name ? formErrors.name[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Email Field */}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter the email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
            validateStatus={formErrors.email ? 'error' : ''}
            help={formErrors.email ? formErrors.email[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Address Field */}
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter the address' }]}
            validateStatus={formErrors.address ? 'error' : ''}
            help={formErrors.address ? formErrors.address[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Phone Field */}
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter the phone number' }]}
            validateStatus={formErrors.phone ? 'error' : ''}
            help={formErrors.phone ? formErrors.phone[0] : ''}
          >
            <Input />
          </Form.Item>

          {/* Password Field */}
          {!selectedAdmin && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter the password' }]}
              validateStatus={formErrors.password ? 'error' : ''}
              help={formErrors.password ? formErrors.password[0] : ''}
            >
              <Input.Password />
            </Form.Item>
          )}

          {/* Password Confirmation Field */}
          {!selectedAdmin && (
            <Form.Item
              name="password_confirmation"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm the password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject('The two passwords do not match');
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          {/* Status Field */}
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select the status' }]}
            validateStatus={formErrors.status ? 'error' : ''}
            help={formErrors.status ? formErrors.status[0] : ''}
          >
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="accepted">Accepted</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {selectedAdmin ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Admins;